import {
  CategoryFormulaKey,
  CategoryFormulaKeyType,
  FieldSuggestion,
  OptimizableFieldType,
  OptimizationResult,
  OptimizeBand,
  OptimizeBandType
} from './ai-optimize-constants';
import { AlexaAssessmentResult } from './alexa-readiness-utils';
import { askClaude } from './scorecard-ai-client';
import {
  allFindings,
  bp,
  bpUpdatedOn,
  deprecatedBlock,
  findingsBlock
} from './scorecard-best-practices-utils';
import { AuditedProduct } from './scoring-engine-constants';
import {
  ITEM_HIGHLIGHT_CHAR_LIMIT,
  TITLE_CHAR_LIMIT,
  TITLE_GENERATION_TARGET
} from './title-compliance-constants';

/** Decide which fields are worth optimizing — skip anything already at full marks. */
export const getOptimizableFields = (product: AuditedProduct): OptimizableFieldType[] => {
  const fields: OptimizableFieldType[] = [];
  if (product.titleScore.status !== 'pass') {
    fields.push('title');
  }
  if (product.bulletsScore.status !== 'pass') {
    fields.push('bullets');
  }
  if (product.descriptionScore.status !== 'pass') {
    fields.push('description');
  }
  return fields;
};

/** Tiered CTA state derived from final score — drives per-row button copy/styling. */
export const getOptimizeBand = (score: number): OptimizeBandType => {
  if (score < 60) return OptimizeBand.PRIORITY;
  if (score < 80) return OptimizeBand.STANDARD;
  if (score < 90) return OptimizeBand.POLISH;
  return OptimizeBand.OPTIMIZED;
};

const isFieldSuggestion = (v: unknown): v is { suggestion: string; reasoning?: unknown } =>
  Boolean(v) &&
  typeof v === 'object' &&
  typeof (v as { suggestion?: unknown }).suggestion === 'string';

const normalizeOptimizationResult = (raw: unknown): OptimizationResult => {
  const r = (raw ?? {}) as Record<string, unknown>;
  const field = (v: unknown): FieldSuggestion | null => {
    if (!isFieldSuggestion(v)) return null;
    return {
      suggestion: v.suggestion,
      reasoning: typeof v.reasoning === 'string' ? v.reasoning : ''
    };
  };
  return {
    title: field(r.title),
    bullets: field(r.bullets),
    description: field(r.description),
    alexaScore:
      typeof r.alexaScore === 'number' ? Math.max(0, Math.min(100, Math.round(r.alexaScore))) : null
  };
};

// ── optimizeListing ───────────────────────────────────────────

const buildOptimizeSystemPrompt = (fields: OptimizableFieldType[]): string => {
  const relevant = allFindings.filter(f => {
    if (f.applies_to === 'alexa_readiness' || f.applies_to === 'both') return true;
    const cat = f.id.split('-')[0];
    return (fields as string[]).includes(cat);
  });

  return `You are an Amazon listing optimization specialist who writes PDP copy explicitly tuned for AI shopping assistants like Amazon's Alexa for Shopping.

CURRENT BEST PRACTICES (sourced from Amazon's official documentation, verified research, and practitioner testing — updated ${bpUpdatedOn}):
${findingsBlock(relevant)}
${deprecatedBlock()}

ADDITIONAL PRINCIPLES:
1. NATURAL CONVERSATIONAL LANGUAGE. Write the way people talk to AI assistants. No keyword stuffing.
2. ANSWER QUERIES SHOPPERS ACTUALLY ASK ("What's the best ___?", "Does this work for ___?", "How is this different from ___?"). Embed answers naturally.
3. LEAD WITH SPECIFIC, CONCRETE BENEFITS. Reference real ingredients, materials, dimensions, scenarios.
4. COVER USE CASES, OCCASIONS, AND COMPARISONS relevant to the category.
5. PRESERVE BRAND VOICE. Do not invent claims not supported by the current copy.
6. RESPECT AMAZON CONSTRAINTS:
   - Title: 60–75 characters (fill Amazon's hard 75-char cap; anything over 75 gets truncated/rejected — never exceed it)
   - Bullets: exactly 5 bullets, each ≤ 150 characters (Alexa truncates at 150 on mobile), separated by " | "
   - Description: 801–2000 characters

ALEXA FOR SHOPPING READINESS SCORE: After writing the rewrites, score the full listing (your proposed copy for rewritten fields + original copy for unchanged fields) 0–100 on Alexa readiness. Criteria:
- Conversational natural language, no keyword stuffing (0–25 pts)
- States clearly who the product is for and what it does better than alternatives (0–25 pts)
- Covers key use cases, occasions, or scenarios a shopper would ask about (0–25 pts)
- Bullets complete (5 filled), each ≤150 chars, opens with customer outcome (0–25 pts)

Return ONLY a JSON object (no markdown, no code fences) of the exact shape:
{"title": {"suggestion": string, "reasoning": string} | null, "bullets": {"suggestion": string, "reasoning": string} | null, "description": {"suggestion": string, "reasoning": string} | null, "alexaScore": number}
Return ONLY the fields explicitly listed in the request as null-safe objects; set unrequested copy fields to null.`;
};

const buildOptimizeUserPrompt = (
  product: AuditedProduct,
  fields: OptimizableFieldType[]
): string => {
  const bulletsPreview =
    product.featureBullets.length > 0
      ? product.featureBullets.map((b, i) => `${i + 1}. ${b}`).join('\n')
      : '(none)';
  const descPreview = !isEmptyPlaceholder(product.fullDescription)
    ? `${product.fullDescription.slice(0, 800)}${product.fullDescription.length > 800 ? '...' : ''}`
    : '(none)';

  return `LISTING CONTEXT
Brand: ${product.brand || '(unknown)'}
Category: ${product.category || '(unknown)'}
Current title (${product.titleScore.actualValue} chars): ${product.title || '(none)'}

Current bullets (${product.featureBullets.length} parsed):
${bulletsPreview}

Current description preview (${product.descriptionScore.actualValue} chars total):
${descPreview}

SCORING DIAGNOSTICS
- Title score: ${product.titleScore.score}/${product.titleScore.maxScore} (${product.titleScore.status})
- Bullets score: ${product.bulletsScore.score}/${product.bulletsScore.maxScore} (${product.bulletsScore.status})
- Description score: ${product.descriptionScore.score}/${product.descriptionScore.maxScore} (${product.descriptionScore.status})

FIELDS NEEDING OPTIMIZATION (rewrite ONLY these; leave the others null in your response):
${fields.map(f => `- ${f}`).join('\n')}

For each field above, provide:
- "suggestion": the rewritten copy (respecting the character limits in your instructions)
- "reasoning": one short sentence on why this rewrite ranks better for AI shopping queries`;
};

export const optimizeListing = async (
  product: AuditedProduct,
  fields: OptimizableFieldType[] = getOptimizableFields(product)
): Promise<OptimizationResult> => {
  if (fields.length === 0) {
    return { title: null, bullets: null, description: null, alexaScore: null };
  }
  const raw = await askClaude({
    system: buildOptimizeSystemPrompt(fields),
    prompt: buildOptimizeUserPrompt(product, fields),
    maxTokens: 2048
  });
  return normalizeOptimizationResult(raw);
};

// ── optimizeTitleForCap ───────────────────────────────────────

/** Which words filled each slot of the Brand→Product→Variant→Size formula. */
export interface TitleSlots {
  brand: string;
  product: string;
  variant: string;
  size: string;
}

export interface TitleRewriteResult {
  title: string;
  /** Slot breakdown of the rewrite (empty string for any slot dropped to fit). */
  slots: TitleSlots;
  reasoning: string;
  /**
   * Banned characters `stripBannedChars` removed from the model's output, for
   * the "what the tool corrected" panel. Reflects the run that produced the
   * returned title, not the discarded attempts before it.
   */
  removedChars: string[];
}

/**
 * Trim to the last whole word that keeps the string within `limit`. Last-resort
 * guard when the model repeatedly overshoots, so the UI always receives a
 * genuinely compliant string. Slices by code point (not UTF-16 code unit) so a
 * surrogate pair (emoji, astral char) at the boundary can't be split into an
 * unpaired half. Shared by the title (75) and item-highlight (125) paths.
 */
export const trimToWordBoundary = (text: string, limit: number = TITLE_CHAR_LIMIT): string => {
  if (text.length <= limit) return text;
  const hardCut = Array.from(text).slice(0, limit).join('');
  const lastSpace = hardCut.lastIndexOf(' ');
  return (lastSpace > 0 ? hardCut.slice(0, lastSpace) : hardCut).trim();
};

/** Characters Amazon auto-corrects/suppresses in titles (Jan-2025 policy). */
const BANNED_TITLE_CHARS = /[!$?_{}^]/g;

interface StrippedTitle {
  title: string;
  /**
   * The distinct banned characters actually removed, in first-seen order. This
   * used to be computed and discarded; the compliance drawer now reports it, so
   * a reviewer can see the tool silently corrected the copy rather than
   * wondering whether a character went missing by accident.
   */
  removed: string[];
}

/**
 * Strip Amazon's banned title characters unless they appear inside the known
 * brand string (a brand may legitimately contain one, e.g. a registered mark).
 * Deterministic guarantee — the prompt asks for this too, but code enforces it.
 */
const stripBannedChars = (title: string, brand: string): StrippedTitle => {
  const brandHasBanned = Boolean(brand) && BANNED_TITLE_CHARS.test(brand);
  BANNED_TITLE_CHARS.lastIndex = 0; // reset after .test()
  if (brandHasBanned) return { title, removed: [] }; // conservative: don't risk mangling the brand

  const removed: string[] = [];
  const stripped = title.replace(BANNED_TITLE_CHARS, match => {
    if (!removed.includes(match)) {
      removed.push(match);
    }
    return '';
  });
  return {
    title: stripped.replace(/\s{2,}/g, ' ').trim(),
    removed
  };
};

/** Coerce an unknown `slots` value from the model into a well-formed TitleSlots. */
const normalizeSlots = (v: unknown): TitleSlots => {
  const r = (v ?? {}) as Record<string, unknown>;
  const s = (x: unknown): string => (typeof x === 'string' ? x : '');
  return { brand: s(r.brand), product: s(r.product), variant: s(r.variant), size: s(r.size) };
};

/** The scraper's "no value scraped" sentinel, in its various whitespace/trim forms. */
const isEmptyPlaceholder = (text: string): boolean =>
  !text || text.trim() === '—' || text === ' — ';

/**
 * Per-category emphasis for the ≤75 title and the item-highlight. The cap is 75
 * everywhere, but WHICH attribute matters — and how much of the space to fill —
 * shifts by category. Formulas come from commerce SME guidance (per-category
 * title/highlight structure), validated in the offline tuning harness through
 * v5. `titleEmphasis` is injected under CATEGORY EMPHASIS in the title prompt;
 * `highlightEmphasis` under the same heading in the highlight prompt.
 * `CATEGORY_FALLBACK` covers anything unmatched. Matched case-insensitively
 * against the product's category + group.
 *
 * Order matters — `.find()` returns the first regex match, so entries with
 * overlapping terms (e.g. "home", "office", "accessor") are resolved by
 * position, not specificity. Reordering or inserting an entry can silently
 * change which formula a category+group string like "Home Fragrance" resolves
 * to, and for WATCHES that precedence is load-bearing rather than incidental —
 * see the note on that row. `resolveCategoryFormulaKey` exposes the resolved
 * row so the ordering can be asserted in tests.
 */
interface CategoryFormula {
  /** Stable id for this row — exported through `resolveCategoryFormulaKey`. */
  key: CategoryFormulaKeyType;
  match: RegExp;
  titleEmphasis: string;
  highlightEmphasis: string;
}

/**
 * Builds the ranked fill ladder appended to every category emphasis.
 *
 * The ladder is the SME answer to titles plateauing in the mid-50s: each
 * formula listed Brand → Product → Variant → Size and then STOPPED, because
 * nothing told the model what was allowed into characters 50-75. It would
 * rather stop than guess — on B0DYBHFBH8 its own reasoning said the listing
 * offered "no additional search-relevant descriptors" while the flavour, pack
 * count and dietary claims sat in the source.
 *
 * Ranked, category-approved, source-only. Signed off by Candice Hernandez
 * 2026-10-05 ("Candice Catch-up Prep", Drive doc
 * 1zE1cKPPEWRwUB3cjV-VOTFtqMWNoBV3J8RlaX7frcyg, section 2).
 *
 * NOTE this is ADDED prompt text, the class of edit that has regressed this
 * file before (see b8ab725b). It is shipped on a measured before/after, not on
 * the reasoning above — see the PR body for the harness numbers.
 */
const fillLadder = (steps: string[]): string =>
  `FILL LADDER — once Brand, Product Type, Variant and Size are placed, if the title is still short of its target band, work DOWN this ranked list: add the highest-ranked item the SOURCE actually states, then the next, until the title is close to the cap. ${steps
    .map((step, i) => `${i + 1}. ${step}`)
    .join(
      ' '
    )} This ladder ranks what to ADD when there is room. It is NOT a list of the only things a title may contain, and it NEVER licenses REMOVING anything. Before you finish, compare your title against the source: if you have dropped a descriptive word the source states and a shopper might search it — a form, a format, an application area, a use — put it back, whether or not it appears on this ladder. Shortening a title that was already a reasonable length is a FAILURE, not a tidy-up. ` +
  `Every item you add must be a fact the source states — never invent one, never add a marketing or benefit claim, and never repeat a word already in the title. If the source states none of these, a short title is the CORRECT answer: stop rather than guess.`;

/**
 * The hardlines formula. WATCHES shared this VERBATIM until 2026-10-19; the two
 * now share only the base and carry different ladders, because the ranked
 * priorities genuinely differ — hardlines leads with the model number, which is
 * the exact thing these Citizen SKUs do not have.
 */
const HARDLINES_BASE =
  'Formula: [Brand] [Main Keyword] [Model Number/Crucial Spec]. ' +
  'There is usually NO shade/flavor variant here — do NOT invent one. The distinguishing attribute is the model number, capacity, size, or piece/pack count. ' +
  `Target length ~${TITLE_GENERATION_TARGET}-${TITLE_CHAR_LIMIT} chars — use the space. ` +
  'Materials and coatings are FEATURES for the highlight, not the variant.';

const HARDLINES_TITLE_EMPHASIS = `${HARDLINES_BASE} ${fillLadder([
  'Model number.',
  'Key spec or capacity (wattage, volume, load rating, piece count).',
  'Dimensions the source states.',
  'Material or build.',
  'Compatibility (what it fits, mounts to, or works with).'
])}`;

/**
 * Watches. Same base as hardlines, different ladder — movement leads, and the
 * complication must survive WHOLE.
 *
 * This is the watch-specific emphasis the WATCHES row comment below previously
 * declined to write, on the grounds that it is the prompt-addition class that
 * regressed PR #1679's change set and "needs its own before/after before anyone
 * ships it". That before/after is the gate on this change, not an afterthought.
 *
 * It targets the known cost of routing watches to hardlines: titles came back
 * 3-17 chars SHORTER and B016R90VBK lost "200M Water Resistant" outright,
 * because the hardlines ladder opens with a model number Citizen does not
 * print. Ranking movement, complication and water resistance ahead of it gives
 * those characters somewhere to go.
 */
const WATCHES_TITLE_EMPHASIS = `${HARDLINES_BASE} ${fillLadder([
  'Movement type (Eco-Drive, automatic, quartz, solar).',
  'Complication, kept WHOLE — never split (3-Hand Date, Chronograph, GMT, Moonphase). If it will not fit in full, drop the whole term rather than strand half of it.',
  'Case size and material (42mm, Stainless Steel, Titanium).',
  'Water resistance (100M, 200M, ISO certified).',
  'Dial and strap colour or material.'
])}`;

const HARDLINES_HIGHLIGHT_EMPHASIS =
  'Lead with: [Material/Build], [Compatibility], [Key Output/Dimension], [Included Items].';

const CATEGORY_MAP: CategoryFormula[] = [
  {
    // Watches / timepieces.
    //
    // MUST stay ahead of the APPAREL row, and this is the ONLY thing that routes
    // watches correctly: Amazon files them under "Clothing, Shoes & Jewelry", so
    // a watch's `hay` (category + group) matches APPAREL's `clothing`/`jewel`
    // terms no matter what that row's regex says. Before this row existed,
    // watches took the apparel formula — [Color] [Size/Fit], which has nowhere
    // to put a movement spec — and Brandi Rice's B016R90VBK (CITIZEN Promaster
    // Dive Eco-Drive) collapsed "3-Hand Date" to a meaningless bare "Date".
    //
    // It shares the hardlines BASE, because a watch IS a spec-and-model
    // product, but since 2026-10-19 it carries its own ranked fill ladder
    // (WATCHES_TITLE_EMPHASIS above) rather than inheriting the hardlines one.
    //
    // MEASURED when the routing landed (same 5-run harness comparison as the
    // consumer row below): B07CGPZD27 went 2/5 -> 0/5 runs dropping "Brycen";
    // B016R90VBK's 3-Hand/Date split went 5/5 -> 3/5 runs; B06XYYF4BG and
    // B071FBZLWN stayed clean.
    //
    // KNOWN COST THE LADDER NOW TARGETS: watch titles came back 3-17 chars
    // SHORTER, and B016R90VBK lost "200M Water Resistant" (58-74 chars before
    // -> 49-52 after), because the hardlines ladder opens with a model number
    // these Citizen SKUs don't print, leaving nothing to spend the remaining
    // characters on. The watch ladder ranks movement, complication and water
    // resistance ahead of the model number for exactly that reason.
    //
    // This is the watch-specific emphasis the previous note here declined to
    // write without its own before/after. That before/after is the gate on the
    // PR shipping it — do not relax it.
    key: CategoryFormulaKey.WATCHES,
    match: /watch|wristwatch|timepiece|chronograph/i,
    titleEmphasis: WATCHES_TITLE_EMPHASIS,
    highlightEmphasis: HARDLINES_HIGHLIGHT_EMPHASIS
  },
  {
    // Consumer goods — food / beauty / supplements
    key: CategoryFormulaKey.CONSUMER,
    match:
      /beauty|cosmetic|makeup|skin|hair|fragrance|food|grocery|beverage|supplement|vitamin|consumable|personal care/i,
    // THE INGREDIENT BAN WAS LIFTED 2026-10-19 ON AN SME RULING. This row used
    // to fuse "NEVER an ingredient, nutrient or claim (no DHA, Omega 3, Vitamin
    // D3, Zinc, Biotin...)" into the Variant definition. Candice Hernandez ruled
    // that a headline nutrient IS part of a supplement's searchable identity
    // ("Omega 3 gummies" is how people search), which is what Brandi Rice's
    // tester rows asked for all along. Relayed by Drew Webb 2026-10-19.
    //
    // BE HONEST ABOUT WHAT THIS REVERTS: the ban was a MEASURED fix. Harness, 18
    // cases x 5 runs, 2026-09-14, archived at
    // s3://tinuiti-dataops-brain/pdp-scorecard/harness-output/2026-09-runs/pr-1679/
    // — B0DYBHFBH8 went 5/5 -> 0/5 runs carrying a callout. Lifting it is
    // expected to put that callout back; that is now the DESIRED behaviour, not
    // a regression. What the before/after must prove is that it comes back as
    // ONE nutrient, not as the DHA/EPA/D3/B12/Zinc keyword soup the Sep 14
    // pre-read warned about. Watch the beauty ASINs (Milani x4, Allies of Skin
    // x4) as much as the supplements: this row covers all 20 consumer ASINs, and
    // nobody asked for ingredient callouts on a highlighter.
    //
    // The ladder admits the nutrient at the LOWEST rank, so it is spent only on
    // characters nothing better claimed. Benefit claims and certifications
    // (Cruelty-Free, Hydrating) stay out — they were never part of the ask, and
    // the global RULES block bans them independently of this row.
    //
    // KNOWN RESIDUAL this is also aimed at: the Sep fix left B0DYBHFBH8 at 51
    // chars WITHOUT the flavour, which this row's own formula calls the variant.
    // Its reasoning showed it SEES "Three Fruit Flavors" and declines to add it.
    // Ranking the descriptors is the lever being tried; if it does not move,
    // the next one is structured variant dimensions as ground truth (b8ab725b),
    // NOT more prompt prose.
    titleEmphasis:
      `Formula: [Brand] [Main Keyword/Product Type] - [Shade/Flavor/Variant] [Size/Pack Count]. ` +
      `Variant = the shade / flavor / scent the SOURCE states. ` +
      `Target length ~${TITLE_GENERATION_TARGET}-${TITLE_CHAR_LIMIT} chars — use the space. ${fillLadder(
        [
          'Product form, format or application area the source states (Gummies, Powder, Serum, Stick, Ground Coffee, for Cheeks, for Dark Circles). Keep these even when the main keyword already implies the form — "Highlighter Powder for Cheeks" is how shoppers search, and trimming it to "Highlighter" loses real search terms.',
          "Target user or life stage the source states (Toddler, Kids, Men's, Adult).",
          'Skin, hair or dietary type the source states (Oily Skin, Color-Treated, Gluten-Free, Vegan).',
          'ONE headline nutrient or key ingredient the source states (Omega 3, Hyaluronic Acid, Collagen) — ONE only, never a stacked list, and never a benefit claim or certification (Cruelty-Free, Hydrating, Clinically Proven), which belong in the highlight. This is an ADDITION, never a swap: it must NOT take the place of the shade/flavor/scent Variant. If both will not fit, the Variant wins and the nutrient is dropped.'
        ]
      )}`,
    highlightEmphasis:
      'Lead with: [Key Ingredient/Material], [Primary Benefit], [Skin/Dietary Type], [Flavor/Scent]. ' +
      'Carry over the certifications and benefit claims that were stripped from the title, using ONLY those the source copy actually states.'
  },
  {
    // Hardlines / electronics — tools / tech / home goods
    key: CategoryFormulaKey.HARDLINES,
    match:
      /electronic|tech|computer|phone|audio|tool|hardware|home|kitchen|cookware|appliance|garden|automotive|office/i,
    titleEmphasis: HARDLINES_TITLE_EMPHASIS,
    highlightEmphasis: HARDLINES_HIGHLIGHT_EMPHASIS
  },
  {
    // Apparel / shoes / jewelry.
    //
    // `watch` was removed from this regex when the WATCHES row was added. That
    // removal alone does NOT route watches away from here (see the note on that
    // row — "Clothing, Shoes & Jewelry" still matches `clothing`); it just
    // stops this row claiming watches by name as well as by category.
    key: CategoryFormulaKey.APPAREL,
    match: /apparel|clothing|shoe|footwear|jewel|accessor|bag/i,
    titleEmphasis:
      `Formula: [Brand] [Gender/Age if it drives search] [Product Type] - [Color] [Size/Fit]. ` +
      `Variant = color, PLUS any secondary SKU-distinguishing dimension the source states (e.g. inseam/leg length, cut, or fit) that the Size slot alone would not capture. ` +
      `Before dropping any length/fit/cut detail to fit the cap, ask: does this brand sell multiple versions that differ ONLY in this detail — say, the same garment offered in two inseam lengths? If dropping it would make this SKU indistinguishable from a sibling listing, it is a decision-driving fact, not filler — preserve it even at the cost of a marketing descriptor added elsewhere. ` +
      `Target length ~${TITLE_GENERATION_TARGET}-${TITLE_CHAR_LIMIT} chars — use the space. ${fillLadder(
        [
          'Fabric or material the source states (Oxford Cotton, Merino Wool, Linen Blend).',
          'Neckline, sleeve or silhouette detail (Button Down, Long Sleeve, Crew Neck, Straight Leg).',
          'Occasion or use the source states (Dress, Casual, Golf, Workout).',
          'Care instruction (Wrinkle Resistant, Machine Washable) — lowest priority, use only if nothing above is available.'
        ]
      )} NONE of these ladder items may be added at the expense of a distinguishing length/fit/cut fact — that fact outranks the entire ladder.`,
    highlightEmphasis:
      'Lead with: [Material/Fabric], [Fit/Cut], [Occasion/Use], [Care]. Include distinguishing style details.'
  }
];

/**
 * Used when nothing in CATEGORY_MAP matches. Covers 5 of the 42 reference ASINs
 * — Dr Brown's bottles x2, Honest Co wipes, the Champion batting tee and
 * Champion dodgeballs — i.e. EXACTLY the rows that keep losing their
 * distinguishing spec (the bottle its capacity, the dodgeball its diameter, the
 * wipes their pack count). They differ from their siblings by precisely the
 * number that gets dropped, and with no formula to say what matters they got
 * generic advice.
 *
 * THIS LADDER IS NOT SME-SIGNED. Candice approved ladders for the four mapped
 * categories only; the fallback was not in front of her. It is synthesised from
 * the two gap-category drafts she DID review (Baby & Kids, Sports & Outdoors /
 * Toys — "Candice Catch-up Prep", section 4), both of which open with the
 * measured dimension and then pack count. Flag it for her sign-off before this
 * is treated as settled, and replace it with real routing rows for Baby & Kids
 * and Sports/Toys when those land — a fallback ladder is a backstop, not a
 * substitute for a formula.
 */
const CATEGORY_FALLBACK: Omit<CategoryFormula, 'match' | 'key'> = {
  titleEmphasis:
    `Variant = the attribute that most distinguishes this item from its siblings. Size = the count/measure shoppers filter on. ` +
    `Target length ~${TITLE_GENERATION_TARGET}-${TITLE_CHAR_LIMIT} chars — use the space. ${fillLadder(
      [
        'The capacity, measured dimension or weight the source states (4oz, 8.5 inch, 2.5 lb) — for these products this is usually the ONLY thing separating the item from its siblings, so it is never filler.',
        'Pack, piece or count (2-Pack, 72 Count).',
        'Age, stage or skill level the source states (Stage 1, Newborn, Ages 8-12, Youth).',
        'Material or construction.',
        'The sport, position, activity or setting the source states (Baseball, Softball, Youth Training, Indoor, Outdoor) — for sporting goods this is often what separates the listing from a general-purpose sibling, so do not treat it as filler.'
      ]
    )}`,
  highlightEmphasis: 'Lead with the single strongest purchase-driver the title had no room for.'
};

/**
 * Which `CATEGORY_MAP` row a product's category + group resolves to. Exported so
 * the compliance view can apply the category-specific title rules (e.g. the
 * consumer-goods ingredient-callout ban) to a finished rewrite, and so the
 * first-match ordering above is directly unit-testable.
 */
export const resolveCategoryFormulaKey = (
  category: string,
  group: string
): CategoryFormulaKeyType => {
  const hay = `${category || ''} ${group || ''}`;
  return CATEGORY_MAP.find(c => c.match.test(hay))?.key ?? CategoryFormulaKey.FALLBACK;
};

/** Resolve a product's category emphasis (SME map row, or the fallback). */
const categoryEmphasis = (
  category: string,
  group: string
): Omit<CategoryFormula, 'match' | 'key'> => {
  const hay = `${category || ''} ${group || ''}`;
  return CATEGORY_MAP.find(c => c.match.test(hay)) ?? CATEGORY_FALLBACK;
};

const buildTitleCapSystemPrompt = (category: string, group: string): string => {
  const titleFindings = findingsBlock(bp.categories.title.findings);
  return `You are an Amazon listing specialist rewriting product titles to comply with Amazon's new title-length cap, effective 2026-07-27.

HARD CONSTRAINT: The rewritten title MUST be ${TITLE_CHAR_LIMIT} characters or fewer, counting spaces. This is a non-negotiable platform limit — a title even one character over will be auto-truncated or suppressed by Amazon. Count carefully.

TITLE STRUCTURE — assemble the title in this slot order. Include a slot only if it applies to this product AND you can keep the whole title under the cap:
1. Brand
2. Product name / type — the core noun a shopper actually searches; keep the FULL product-line name. Every word between the brand and that core noun is candidate NAMING, of two kinds. (a) Line, collection or series names: ordinary-sounding words a brand has adopted as a name — adjective-shaped ones ("Performance", "Stretch", "Pinpoint", "Advantage", "Pro", "Plus", "One") and ordinary nouns ("Weekender", "Promaster", "SuperBreak"). (b) Plain physical descriptors of the item: sleeve length, pattern, neckline, material, colour, count. PRESERVE (a) even where the word also reads as a generic adjective or noun — it is the fact that separates this SKU from its siblings — and drop (b) first when you need room. A name word does NOT have to sit next to the brand or the product noun to qualify: if it sits inside a run of other name words, it belongs to the name.
3. Variant — the shade / flavor / scent / color / model / style that distinguishes THIS item from its siblings
4. Size / volume / pack count
5. Search Descriptor(s) — one or more high-value category, use-case, or spec descriptors a shopper would actually search. Use these to bring the title up into its target band whenever slots 1-4 leave it short of the cap.
Fill earlier slots first. If you exceed the cap, drop from the BACK (size first, then variant). Brand and product type are never dropped.

USE THE AVAILABLE SPACE — a compliant title should also be a COMPLETE one. Amazon ranks and converts better when the title uses the room it has, so aim to land inside the target length band for this category (see CATEGORY EMPHASIS) rather than stopping at the bare minimum. Draw the extra descriptors from the source bullets/description. Never add marketing/benefit claims, never invent facts, and never repeat a word to fill space — claims belong in the highlight, not the title.

RULES:
- Strip words that are clearly promotional filler (superlatives, "new", "best-selling") or that duplicate another word already in the title. A duplicate means the SAME word repeated — a shared word stem is NOT a duplicate, so the presence of "Highlighter" does not license collapsing "Highlight Powder" into "Powder". Do NOT strip a word solely because it sounds like a marketing adjective — check the product-line rule above first: a word that is part of the product's actual model/style name is a fact, not fluff, even when it also reads as an adjective. Preserve facts; never invent a claim, spec, or certification not present in the input.
- Do not repeat any single word more than twice (articles, prepositions, conjunctions are exempt).
- Keep multi-word specs and model names WHOLE, and never shorten or abbreviate a word the source spells out (Multivitamin stays Multivitamin, not Multi; Stainless Steel stays Stainless Steel, not Steel). When such a term will not fit in full, drop the ENTIRE term rather than keep part of it — a lone qualifier, unit or number stranded from the term it belonged to tells a shopper nothing and reads as an error.
- Do NOT use ! $ ? _ { } ^ unless the character is genuinely part of the registered brand name. No ALL-CAPS words for emphasis.
- Place the shade / color / variant / size / pack count at the very END of the title, preceded by a hyphen ( - ).
- The result must read as a natural noun phrase a shopper would say aloud, not a keyword string.

CATEGORY EMPHASIS for this product's category:
${categoryEmphasis(category, group).titleEmphasis}

TITLE BEST PRACTICES (updated ${bpUpdatedOn}):
${titleFindings}

Return ONLY a JSON object (no markdown, no code fences) of the exact shape: {"title": string, "slots": {"brand": string, "product": string, "variant": string, "size": string}, "reasoning": string}. Use an empty string for any slot you dropped to fit. Do not include the character count in the title text itself.`;
};

const buildTitleCapUserPrompt = (product: AuditedProduct, extraNudge = ''): string => {
  const bulletsPreview =
    product.featureBullets.length > 0
      ? product.featureBullets.map((b, i) => `${i + 1}. ${b}`).join('\n')
      : '(none)';
  const desc = (product.fullDescription || '').trim();
  const descriptionPreview = !isEmptyPlaceholder(desc)
    ? `${desc.slice(0, 600)}${desc.length > 600 ? '…' : ''}`
    : '(none)';
  const len = isEmptyPlaceholder(product.title) ? 0 : product.title.length;
  const overBy = Math.max(0, len - TITLE_CHAR_LIMIT);

  return `LISTING CONTEXT
Brand: ${product.brand || '(unknown)'}
Category: ${product.category || '(unknown)'}
Current title (${len} chars — ${overBy} over the ${TITLE_CHAR_LIMIT} cap): ${product.title || '(none)'}

Key selling points (bullets — context for what matters, do NOT copy verbatim):
${bulletsPreview}

Product description (additional attribute context, do NOT copy verbatim):
${descriptionPreview}

TASK: Rewrite this title to ${TITLE_CHAR_LIMIT} characters or fewer using the slot structure, keeping the most search-relevant, decision-driving information.${extraNudge}`;
};

export interface OptimizeTitleOptions {
  /**
   * Rewrite even when the title is already within the cap. Off by default:
   * testers reported the tool compressing compliant titles and losing real
   * search terms (Milani 72→61, Allies 67→64, every run), and in those rows the
   * tester's "better title" WAS the original. Only set this from an explicit
   * per-row user action that asks for a rewrite anyway.
   */
  force?: boolean;
}

/**
 * Worst case makes up to 4 `askClaude` calls (initial run, over-cap retry,
 * expand-toward-target run, and a second over-cap retry on the expanded
 * result) — called per-product in batch flows, so cost/latency scale
 * accordingly for large batches. An already-compliant title short-circuits to
 * zero calls unless `force` is set.
 */
export const optimizeTitleForCap = async (
  product: AuditedProduct,
  { force = false }: OptimizeTitleOptions = {}
): Promise<TitleRewriteResult> => {
  // Already within the cap → nothing to fix. Returning the original untouched
  // is both the correct answer and free; compressing it only destroys search
  // terms the brand deliberately put there.
  //
  // Counted with `.length` (UTF-16 units) rather than by code point, to stay
  // byte-for-byte consistent with classifyTitle() — otherwise the length badge
  // in the UI and this gate could disagree on an astral-plane character. Not
  // imported from title-compliance-utils because that module imports TitleSlots
  // from here, and a runtime circular import is not worth the reuse.
  const currentLength = isEmptyPlaceholder(product.title) ? 0 : product.title.length;
  if (!force && currentLength > 0 && currentLength <= TITLE_CHAR_LIMIT) {
    return {
      title: product.title,
      slots: normalizeSlots(undefined),
      reasoning: `Already ${currentLength}/${TITLE_CHAR_LIMIT} characters — left unchanged.`,
      removedChars: []
    };
  }

  const run = async (extraNudge = ''): Promise<TitleRewriteResult> => {
    const raw = await askClaude({
      system: buildTitleCapSystemPrompt(product.category, product.group),
      prompt: buildTitleCapUserPrompt(product, extraNudge),
      maxTokens: 2048
    });
    const r = (raw ?? {}) as Record<string, unknown>;
    if (typeof r.title !== 'string') {
      throw new Error('Model response missing title field.');
    }
    // Deterministic banned-char guarantee, regardless of what the model returned.
    const stripped = stripBannedChars(r.title, product.brand);
    return {
      title: stripped.title,
      slots: normalizeSlots(r.slots),
      reasoning: typeof r.reasoning === 'string' ? r.reasoning : '',
      removedChars: stripped.removed
    };
  };

  let result = await run();

  // Over-cap guarantee: one stricter retry before falling back to a mechanical
  // word-boundary trim, so the UI always receives a genuinely compliant string.
  const enforceCap = async (): Promise<void> => {
    if (result.title.length > TITLE_CHAR_LIMIT) {
      result = await run(
        `\n\nYour previous attempt was ${result.title.length} characters — that EXCEEDS the ${TITLE_CHAR_LIMIT}-character cap. Shorten it further. Previous attempt: "${result.title}"`
      );
    }
    if (result.title.length > TITLE_CHAR_LIMIT) {
      result = {
        ...result,
        title: trimToWordBoundary(result.title),
        reasoning: `${result.reasoning} (auto-trimmed to fit the cap)`
      };
    }
  };
  await enforceCap();

  // Too-short guarantee (mirror of the over-cap retry): if the model under-used
  // the cap, give it ONE pass to expand toward the target, working down the
  // category FILL LADDER in the system prompt. It may keep a short title only if
  // there is genuinely nothing source-stated left to add — we never pad.
  //
  // Triggers on TITLE_GENERATION_TARGET (65), NOT TITLE_TARGET_MIN (55). Those
  // were the same number until 2026-10-19, and that is precisely why output
  // plateaued in the mid-50s: a 56-char title cleared the floor, so this pass
  // never fired and Portfolio Health still paid it full credit. The grading
  // floor deliberately stays at 55 so the grader is never STRICTER than the
  // generator — see TITLE_GENERATION_TARGET's docstring.
  if (result.title.length < TITLE_GENERATION_TARGET) {
    const prev = result;
    result = await run(
      `\n\nYour previous title used only ${prev.title.length} of the ${TITLE_CHAR_LIMIT} available characters — short of the ~${TITLE_GENERATION_TARGET}-${TITLE_CHAR_LIMIT} target band. Work DOWN the FILL LADDER in your CATEGORY EMPHASIS, adding the highest-ranked item the source actually states, then the next, until the title is close to the ${TITLE_CHAR_LIMIT}-character cap. Add ONLY facts the source states. Do NOT add marketing/benefit claims, invent facts, or repeat words. Keep it short ONLY if the source genuinely states none of the ladder items. Previous attempt: "${prev.title}"`
    );
    await enforceCap(); // an expansion could overshoot — re-apply the cap guarantee

    // KEEP THE BETTER OF THE TWO. The expand pass is a full REGENERATION, not an
    // edit of `prev` — the model re-derives the title from scratch with an extra
    // nudge, so it can legitimately come back SHORTER than what it replaced.
    // Nothing used to stop that shorter re-roll from winning, which made the
    // pass a coin flip rather than a one-way improvement.
    //
    // Measured 2026-10-19, frozen-25, before/after the fill ladders: titles
    // below the floor went 3 -> 6 and three cases collapsed outright —
    // B000LXWHDY 71 -> 53 (lost "Baseball Softball"), B0009RFB76 63 -> 46,
    // B00GNBGNBA 59 -> 54. Mean length moved just +0.6 chars because the gains
    // and the collapses cancelled. Raising the trigger from 55 to 65 did not
    // cause this; it exposed it, by putting far more titles through the re-roll
    // (29 -> 40 model calls).
    //
    // `prev` is already cap-enforced, so reverting to it is always safe. This is
    // a deterministic guard in CODE, deliberately not more prompt text — the
    // edit class that holds up here is removals, tightening and guarantees.
    if (result.title.length < prev.title.length) {
      result = {
        ...prev,
        reasoning: `${prev.reasoning} (an expansion pass was discarded: it came back ${prev.title.length - result.title.length} chars shorter)`
      };
    }
  }

  return result;
};

// ── optimizeItemHighlight ─────────────────────────────────────
// Amazon's 2026 PDP format pairs the ≤75-char title with a separate item-detail /
// "item highlight" field, capped at ≤125 chars. NET-NEW field (the scraper never
// captured it) — we GENERATE it to COMPLEMENT (not repeat) the compliant title.
// Same guarantee-in-code pattern as optimizeTitleForCap: one stricter retry on
// overshoot, then a word-boundary trim, so the result is ALWAYS ≤125.

export interface HighlightRewriteResult {
  /** Item-highlight text, guaranteed ≤ ITEM_HIGHLIGHT_CHAR_LIMIT characters. */
  highlight: string;
  reasoning: string;
}

const buildHighlightSystemPrompt = (category: string, group: string): string => {
  const titleFindings = findingsBlock(bp.categories.title.findings);
  return `You are an Amazon listing specialist writing the item-highlight (item-detail) field for the 2026 PDP format.

HARD CONSTRAINT: The highlight MUST be ${ITEM_HIGHLIGHT_CHAR_LIMIT} characters or fewer, counting spaces. Amazon truncates or suppresses anything longer. Count carefully.

PRIORITIES, in order:
1. STAY AT OR UNDER ${ITEM_HIGHLIGHT_CHAR_LIMIT} CHARACTERS. Nothing else matters if you exceed it.
2. Lead with the single strongest purchase-driver or differentiator for this product.
3. COMPLEMENT the title — do NOT repeat the brand, model, or wording already in the proposed title. Add the decision-relevant detail the title had no room for.
4. Plain, natural shopper language. No keyword stuffing, no ALL CAPS, no invented claims.

CATEGORY EMPHASIS for this product's category:
${categoryEmphasis(category, group).highlightEmphasis}

TITLE / LISTING BEST PRACTICES (updated ${bpUpdatedOn}):
${titleFindings}

Return ONLY a JSON object (no markdown, no code fences) of the exact shape: {"highlight": string, "reasoning": string}. Do not include the character count in the highlight text itself.`;
};

const buildHighlightUserPrompt = (
  product: AuditedProduct,
  proposedTitle: string,
  extraNudge = ''
): string => {
  const bulletsPreview =
    product.featureBullets.length > 0
      ? product.featureBullets.map((b, i) => `${i + 1}. ${b}`).join('\n')
      : '(none)';

  // Feed the product description too — it carries attribute detail the bullets
  // and the compliant title often miss.
  const desc = (product.fullDescription || '').trim();
  const descBlock = !isEmptyPlaceholder(desc)
    ? `\nProduct description (additional detail to draw from — do NOT copy verbatim):\n${desc.slice(0, 600)}${desc.length > 600 ? '…' : ''}\n`
    : '';

  // Surface the original (often over-cap) title so the highlight can carry the
  // claims/attributes the compliant title had to drop — the "overflow" concept.
  // Skip when there's no scraped title at all, so the placeholder sentinel
  // doesn't get surfaced to the model as a fake "original title".
  const overflowBlock =
    !isEmptyPlaceholder(product.title) && product.title !== proposedTitle
      ? `\nOriginal (over-cap) title — surface the important details from here that the new compliant title had to drop:\n${product.title}\n`
      : '';

  return `LISTING CONTEXT
Brand: ${product.brand || '(unknown)'}
Category: ${product.category || '(unknown)'}
Proposed compliant title (${proposedTitle.length} chars — complement this, do NOT repeat it): ${proposedTitle || '(none)'}
${overflowBlock}${descBlock}
Bullets (context for what to highlight — do not copy verbatim):
${bulletsPreview}

TASK: Write one item-highlight line of ${ITEM_HIGHLIGHT_CHAR_LIMIT} characters or fewer that complements the title above with the most decision-relevant detail it doesn't already state.${extraNudge}`;
};

/**
 * Generate a single product's item-highlight to fit the ≤125-char cap. Pass the
 * accepted ≤75 rewrite as `proposedTitle` when one exists so the highlight
 * complements the compliant title rather than the over-cap original.
 */
export const optimizeItemHighlight = async (
  product: AuditedProduct,
  proposedTitle: string = product.title
): Promise<HighlightRewriteResult> => {
  const run = async (extraNudge = ''): Promise<HighlightRewriteResult> => {
    const raw = await askClaude({
      system: buildHighlightSystemPrompt(product.category, product.group),
      prompt: buildHighlightUserPrompt(product, proposedTitle, extraNudge),
      maxTokens: 1024
    });
    const r = (raw ?? {}) as Record<string, unknown>;
    if (typeof r.highlight !== 'string') {
      throw new Error('Model response missing highlight field.');
    }
    return {
      highlight: r.highlight,
      reasoning: typeof r.reasoning === 'string' ? r.reasoning : ''
    };
  };

  let result = await run();
  if (result.highlight.length > ITEM_HIGHLIGHT_CHAR_LIMIT) {
    result = await run(
      `\n\nYour previous attempt was ${result.highlight.length} characters — that EXCEEDS the ${ITEM_HIGHLIGHT_CHAR_LIMIT}-character cap. Shorten it further. Previous attempt: "${result.highlight}"`
    );
  }
  if (result.highlight.length > ITEM_HIGHLIGHT_CHAR_LIMIT) {
    result = {
      highlight: trimToWordBoundary(result.highlight, ITEM_HIGHLIGHT_CHAR_LIMIT),
      reasoning: `${result.reasoning} (auto-trimmed to fit the cap)`
    };
  }
  return result;
};

// ── alexaImproveListing ───────────────────────────────────────

const buildAlexaImproveSystemPrompt = (): string => {
  const alexaFindings = bp.categories.alexa_for_shopping.findings;
  return `You are an Amazon listing specialist improving copy for Alexa for Shopping (Amazon's AI shopping assistant).

SCOPE: Qualitative improvements ONLY.
Structural compliance (title length, bullet count, description length) is ALREADY VERIFIED by a separate Health pass — do NOT change character counts to meet minimums/maximums, do NOT flag length issues, and do NOT add or remove bullets.
Your only job: improve conversational naturalness, who-it's-for clarity, use-case coverage, and voice search discoverability.

CURRENT BEST PRACTICES — Alexa for Shopping (updated ${bpUpdatedOn}):
${findingsBlock(alexaFindings)}
${deprecatedBlock()}

PRINCIPLES:
1. CONVERSATIONAL LANGUAGE. Sound like a knowledgeable friend answering a spoken question — not a keyword list.
2. WHO IT'S FOR + DIFFERENTIATION. Make explicit what buyer this suits and what sets it apart.
3. USE CASES AND OCCASIONS. Embed answers to questions shoppers actually ask: "best for ___?", "works with ___?", "vs ___?".
4. PRESERVE BRAND VOICE AND FACTS. Do not invent claims not in the current copy.
5. RETURN NULL for fields already performing well on voice quality — only suggest rewrites that meaningfully improve discoverability.

Return the projected alexaScore (0–100) after your proposed improvements.

Return ONLY a JSON object (no markdown, no code fences) of the exact shape:
{"title": {"suggestion": string, "reasoning": string} | null, "bullets": {"suggestion": string, "reasoning": string} | null, "description": {"suggestion": string, "reasoning": string} | null, "alexaScore": number}`;
};

const buildAlexaImproveUserPrompt = (
  product: AuditedProduct,
  assessment: AlexaAssessmentResult
): string => {
  const bulletsPreview =
    product.featureBullets.length > 0
      ? product.featureBullets.map((b, i) => `${i + 1}. ${b}`).join('\n')
      : '(none)';
  const descPreview = !isEmptyPlaceholder(product.fullDescription)
    ? `${product.fullDescription.slice(0, 800)}${product.fullDescription.length > 800 ? '…' : ''}`
    : '(none)';
  const dimBlock = assessment.dimensions
    .map(d => `  • ${d.label}: ${d.score}/25 — ${d.rationale}`)
    .join('\n');

  return `LISTING CONTEXT (effective copy — includes any prior Health optimizations)
Brand: ${product.brand || '(unknown)'}
Category: ${product.category || '(unknown)'}
Title (${product.title.length} chars): ${product.title || '(none)'}

Bullets (${product.featureBullets.length}):
${bulletsPreview}

Description preview (${(product.fullDescription ?? '').length} chars):
${descPreview}

CURRENT ALEXA FOR SHOPPING ASSESSMENT (${assessment.score}/100):
${dimBlock}
Biggest gap: ${assessment.summary}

TASK: Improve the weakest dimensions above. Do NOT touch structure.
For each field you improve:
  - "suggestion": rewritten copy (preserve approximate length and bullet count)
  - "reasoning": one sentence on what voice-search gap this closes`;
};

export const alexaImproveListing = async (
  product: AuditedProduct,
  assessment: AlexaAssessmentResult
): Promise<OptimizationResult> => {
  const raw = await askClaude({
    system: buildAlexaImproveSystemPrompt(),
    prompt: buildAlexaImproveUserPrompt(product, assessment),
    maxTokens: 2048
  });
  return normalizeOptimizationResult(raw);
};

export type { FieldSuggestion, OptimizationResult };
